import dayjs from 'dayjs'
import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTable, CellProps, Column, Cell, Row, HeaderGroup } from 'react-table'

import { SxProps, Theme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardMedia from '@mui/material/CardMedia'
import Chip, { ChipProps } from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import NoResults from '@/assets/svg/no-results.svg?react'
import LaunchIcon from '@mui/icons-material/Launch'

import ChipButton from '@/components/ui/buttons/ChipButton'
import CalendarIcon from '@/components/ui/icons/Calendar'
import { redirectTo } from '@/utils/consultations'
import { ConsultationStatus, ConsultationType } from '@/core/models/consultation.model'
import { Button, CardContent, CardHeader } from '@mui/material'
import ConsultationIcon from '@/components/ui/icons/Consultation'
import colors from '@/assets/scss/_themes-vars.module.scss'

export type LocalConsultationData = {
  id: string
  name?: string
  surname?: string
  gender?: string
  startDate: string
  status: ConsultationStatus
  type: ConsultationType
}

export type ConsultationsCardProps = {
  consultations: LocalConsultationData[]
  emptyMessage: string
  sx?: SxProps<Theme>
  onCreateConsultation?: () => void
}

const textStyles = {
  fontSize: '12px',
}

export default function LocalConsultationsCard(props: ConsultationsCardProps) {
  const { consultations, emptyMessage, sx = [], onCreateConsultation } = props
  const navigate = useNavigate()
  const { t } = useTranslation()

  const columns = useMemo(() => {
    return [
      {
        Header: t('field.name.label').toUpperCase(),
        accessor: 'surname',
        Cell: ({ value: surname }: CellProps<LocalConsultationData, string>) => (
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '15px' }}>
            {surname}
          </Typography>
        ),
      },
      {
        Header: t('field.firstName.label').toUpperCase(),
        accessor: 'name',
        Cell: ({ value: name }: CellProps<LocalConsultationData, string>) => (
          <Typography variant="body2" sx={{ color: colors.gray600, fontSize: '15px' }}>
            {name}
          </Typography>
        ),
      },
      {
        Header: t('field.gender.label').toUpperCase(),
        accessor: 'gender',
        Cell: ({ value: gender }: CellProps<LocalConsultationData, string>) => {
          if (!gender) return null
          let color: ChipProps['color']
          switch (gender) {
            case 'M':
              color = 'primary'
              break
            case 'F':
              color = 'secondary'
              break
            default:
              color = 'info'
              break
          }
          return <Chip color={color} label={gender} size="small" variant="filled" />
        },
      },
      {
        Header: t('field.date.label').toUpperCase(),
        accessor: 'startDate',
        Cell: ({ value }: CellProps<LocalConsultationData, LocalConsultationData['startDate']>) => (
          <Stack alignItems="center" direction="row" spacing={1}>
            <CalendarIcon fontSize="small" />
            <Chip
              color="secondary"
              label={
                <span>
                  {dayjs(value).format('DD/MM/YYYY') + ' '}
                  <b>{dayjs(value).format('HH[h]mm')}</b>
                </span>
              }
              variant="filled"
              sx={[textStyles]}
            />
          </Stack>
        ),
      },
      {
        Header: t('field.status.label').toUpperCase(),
        accessor: 'status',
        Cell: ({ value, row: { original: consultation } }: CellProps<LocalConsultationData, string>) => {
          let color: ChipProps['color']
          let status: string = value
          switch (value) {
            case 'scheduled':
              color = 'primary'
              break
            case 'accepted':
              color = 'success'
              break
            case 'canceled':
            case 'refused':
              color = 'error'
              break
            default:
              color = 'info'
              break
          }
          return <Chip color={color} label={t(`common:model.consultation.status.${status}.label`)} size="small" variant="filled" />
        },
      },
    ] as Column<LocalConsultationData>[]
  }, [t])

  const { getTableBodyProps, headerGroups, prepareRow, rows } = useTable<LocalConsultationData>({
    columns,
    data: consultations,
  })

  const handleOpenConsultation = (row: Row<LocalConsultationData>) => () => {
    navigate(
      redirectTo({
        _id: row.original.id,
        type: row.original.type,
        status: row.original.status,
      })
    )
  }

  return (
    <Card sx={{ ...sx, height: '100%' }}>
      <CardHeader
        title={t('page.home.consultations.local')}
        action={
          <Button
            sx={{ border: 'none', background: 'radial-gradient(circle, rgba(42,182,183,1) 0%, rgba(37,154,164,1) 100%)' }}
            onClick={onCreateConsultation}
            variant="contained"
            startIcon={<ConsultationIcon htmlColor="#FFF" />}
          >
            {t('page.home.consultations.createConsultationButton.local')}
          </Button>
        }
      />
      <CardContent>
        <Stack direction="column" justifyContent="space-between" sx={{ height: '100%' }}>
          <Box sx={{ padding: '0 24px 24px 24px' }}>
            {!consultations.length ? (
              <Stack direction="column" justifyContent="center" alignItems="center">
                <CardMedia component={NoResults} sx={{ width: 'auto', height: '250px', mt: '10px', mr: '40px' }} />
                <Typography component="div" variant="body1">
                  {emptyMessage}
                </Typography>
              </Stack>
            ) : (
              <TableContainer sx={{ maxHeight: '325px' }}>
                <Table stickyHeader sx={{ minWidth: '650px' }}>
                  <TableHead>
                    {headerGroups.map((headerGroup: HeaderGroup<LocalConsultationData>) => (
                      <TableRow {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column: HeaderGroup<LocalConsultationData>) => {
                          return (
                            <TableCell {...column.getHeaderProps()} scope="col">
                              {column.render('Header')}
                            </TableCell>
                          )
                        })}
                        <TableCell align="right">{t('common:table.header.action').toUpperCase()}</TableCell>
                      </TableRow>
                    ))}
                  </TableHead>
                  <TableBody {...getTableBodyProps()}>
                    {rows.map((row) => {
                      prepareRow(row)
                      return (
                        <TableRow {...row.getRowProps()}>
                          {row.cells.map((cell: Cell<LocalConsultationData>) => (
                            <TableCell {...cell.getCellProps()} align="left" sx={{ borderBottom: 'none' }}>
                              {cell.render('Cell')}
                            </TableCell>
                          ))}
                          <TableCell align="right" sx={{ borderBottom: 'none' }}>
                            <Stack justifyContent="flex-end" direction="row" spacing={1}>
                              <Tooltip title={t('page.home.consultations.openConsultationButton.tooltip')}>
                                <ChipButton color="info" onClick={handleOpenConsultation(row)}>
                                  <LaunchIcon />
                                </ChipButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}
